import React from "react";
import PropTypes from "prop-types";
import { AutocompleteField, Form as FormikForm, InputField, TextareaField } from "components/common_components/Formik";
import Grid from "@material-ui/core/Grid";
import * as Yup from "yup";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import { Field } from "formik";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";

function Form({ data, resources, mode, ...rest }) {
    const validationSchema = Yup.object({
        id: Yup.string().required("Required field").max(100, "Max length is 100 characters"),
        name: Yup.string().required("Required field").max(100, "Max length is 100 characters"),
        description: Yup.string().notRequired().nullable().max(2000, "Max length is 2000 characters"),
        permissions: Yup.array().of(Yup.object()),
    });

    const initialValues = {
        id: data.id,
        name: data.name,
        description: data.description || "",
        permission: null,
        permissions: data.permissions,
    };

    return (
        <FormikForm initialValues={initialValues} validationSchema={validationSchema} {...rest}>
            {(form) => (
                <>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8} lg={6}>
                            <InputField
                                disabled={mode === "update"}
                                fieldName="id"
                                label="Identifier *"
                                uppercase
                                fullWidth={false}
                                maxLength={100}
                            />
                            <InputField fieldName="name" label="Name *" maxLength={200} />
                            <TextareaField fieldName="description" label="Description" maxLength={2000} rows={3} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} direction="row" alignItems="center">
                        <Grid item xs={11} md={10} lg={8}>
                            <AutocompleteField
                                fieldName="permission"
                                options={resources.permissions.filter((e) => !form.values.permissions.some((p) => p.id === e.id))}
                                valueKey="id"
                                labelKey="name"
                                renderOption={(option) => (
                                    <div className="permission">
                                        <span>{option.groupOfPermissions}</span>
                                        {" - "}
                                        <b>{option.name}</b>
                                    </div>
                                )}
                                getOptionLabel={(option) => `${option.groupOfPermissions} - ${option.name}`}
                                label="Choose a permission and click on the plus sign to add it"
                            />
                        </Grid>
                        <Grid item xs={1}>
                            {form.values.permissions && (
                                <Button
                                    outline
                                    small
                                    secondary
                                    onClick={() => {
                                        form.setFieldValue("permissions", [...form.values.permissions, form.values.permission]);
                                        form.setFieldValue("permission", null);
                                    }}
                                >
                                    <Icon name="add" size="sm" color="secondary" />
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Field name="permissions">
                            {({ field }) => (
                                <EnhancedTable
                                    headCells={[
                                        { index: 0, id: "groupOfPermissions", label: "Scope" },
                                        {
                                            index: 1,
                                            id: "name",
                                            label: "Permission",
                                            render: (_, { data }) => (
                                                <div className="permission">
                                                    <b>{data.name}</b>
                                                    <span className="id">{data.id}</span>
                                                    <span className="description">{data.description}</span>
                                                </div>
                                            ),
                                        },
                                        {
                                            index: 2,
                                            id: "actions",
                                            label: "",
                                            render: (_, { data }) => (
                                                <>
                                                    {
                                                        <Grid container spacing={1} direction="row" alignItems="center">
                                                            <Grid item md={12} lg={4}>
                                                                <Button
                                                                    outline
                                                                    small
                                                                    secondary
                                                                    onClick={() =>
                                                                        form.setFieldValue(
                                                                            "permissions",
                                                                            field.value.filter((p) => p.id !== data.id)
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon name="delete" size="sm" color="secondary" />
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                    }
                                                </>
                                            ),
                                        },
                                    ]}
                                    data={field.value}
                                    selectable={false}
                                    pageable={false}
                                    filterable={false}
                                    deletable={false}
                                    orderable={false}
                                    keyColumnName="id"
                                    onRequestData={() => field.value}
                                />
                            )}
                        </Field>
                    </Grid>
                </>
            )}
        </FormikForm>
    );
}

Form.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        permissions: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                groupOfPermissions: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            })
        ),
    }),
    resources: PropTypes.shape({
        permissions: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                groupOfPermissions: PropTypes.string,
                name: PropTypes.string,
                description: PropTypes.string,
            })
        ),
    }),
    mode: PropTypes.oneOf(["add", "update"]).isRequired,
};

Form.defaultProps = {
    data: {
        id: "",
        name: "",
        description: "",
        permissions: [],
    },
    resources: {
        permissions: [],
    },
};

export default Form;
